import {
  type AttemptLocks,
  type AttemptStorage,
  browserAttemptStorage,
  createUnresolvedAttemptStore,
  mayHaveTakenEffect,
  reconcileAttemptAtSend,
  withAttemptLock,
} from "@/shared/utils/unresolvedAttempt";

import type { CreateWalletTopUpPayload } from "../types/walletTopUps.types";

/** A top-up sent but whose outcome is not known yet: kept until the backend says what became of it. */
export interface WalletTopUpAttempt {
  payload: CreateWalletTopUpPayload;
  /** For the notice; the wallet list may not be loaded when it shows. */
  currency: string | null;
}

export type WalletTopUpLookup = "not_placed" | "pending" | "unknown";

export const WALLET_TOP_UP_NOT_FOUND_CODE = "wallet_top_up_not_found";
export const WALLET_TOP_UP_IN_PROGRESS_CODE = "wallet_top_up_in_progress";

/** After a failed create: true when it may still have credited, so the attempt must be kept. */
export const mayHaveCredited = mayHaveTakenEffect;

/** What a failed lookup by key means. Only these two codes are answers; anything else is not knowing yet. */
export function settleWalletTopUpLookup(status: number | null, code: string | null): WalletTopUpLookup {
  if (status === 404 && code === WALLET_TOP_UP_NOT_FOUND_CODE) return "not_placed";
  if (status === 409 && code === WALLET_TOP_UP_IN_PROGRESS_CODE) return "pending";
  return "unknown";
}

type TopUpBody = Omit<CreateWalletTopUpPayload, "idempotency_key">;

/** The same top-up as the stored one: retrying it must reuse the stored key, so the two can never both credit. */
export function isSameTopUp(stored: CreateWalletTopUpPayload, next: TopUpBody): boolean {
  return (
    stored.organization_id === next.organization_id &&
    stored.wallet_id === next.wallet_id &&
    stored.amount.trim() === next.amount.trim() &&
    stored.concept.trim() === next.concept.trim() &&
    stored.internal_note.trim() === next.internal_note.trim()
  );
}

/**
 * Right before a top-up leaves, against what the store holds NOW — another tab may have sent one to the same
 * organization while this challenge was open, and its answer may be lost. `null` refuses: a different top-up
 * is unresolved. Otherwise the payload to send; `adopted` when it is the unresolved top-up itself (same
 * body), sent with ITS key so the backend replays it instead of crediting a second time.
 */
export function reconcileTopUpAtSend(
  stored: WalletTopUpAttempt | null,
  outgoing: CreateWalletTopUpPayload,
): { payload: CreateWalletTopUpPayload; adopted: boolean } | null {
  return reconcileAttemptAtSend(stored, outgoing, isSameTopUp);
}

/** Refused before sending: another tab held the organization's top-up lock for too long. */
export class WalletTopUpBusyError extends Error {
  constructor() {
    super("Another tab is sending a top-up to this organization");
    this.name = "WalletTopUpBusyError";
  }
}

/**
 * Runs one send of a top-up holding the organization's lock, shared by every tab of this browser (Web Locks):
 * a second tab waits for the first request to settle, then reads the store again, so it can never send
 * another key while the first answer is still out. Waits at most 30 s (`WalletTopUpBusyError`); runs unlocked
 * where the browser has no lock manager.
 */
export function withWalletTopUpLock<T>(
  organizationId: string,
  run: () => Promise<T>,
  locks?: AttemptLocks | null,
  waitMs?: number,
): Promise<T> {
  return withAttemptLock(
    `coloca.walletTopUp:${organizationId}`,
    run,
    () => new WalletTopUpBusyError(),
    locks,
    waitMs,
  );
}

const STORAGE_KEY = "coloca.walletTopUp.unresolved";

const isAttempt = (value: unknown): value is WalletTopUpAttempt => {
  const attempt = value as Partial<WalletTopUpAttempt> | null;
  const payload = attempt?.payload;
  return (
    !!payload &&
    typeof payload.organization_id === "string" &&
    typeof payload.wallet_id === "string" &&
    typeof payload.amount === "string" &&
    typeof payload.concept === "string" &&
    typeof payload.internal_note === "string" &&
    typeof payload.idempotency_key === "string"
  );
};

/**
 * One unresolved attempt per organization. Survives closing the dialog and a reload: written before the
 * request leaves, removed only once its outcome is known. Falls back to memory when storage is refused.
 */
export function createUnresolvedTopUpStore(storage: AttemptStorage | null) {
  return createUnresolvedAttemptStore(storage, STORAGE_KEY, isAttempt);
}

export const unresolvedWalletTopUps = createUnresolvedTopUpStore(browserAttemptStorage());
